import React, { useState, useRef, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { useQuotes } from '../context/QuotesContext';
import { Trash2, Pencil } from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { 
  profileSystems, 
  glassTextures, 
  glazingTypes, 
  colors, 
  raamtypes, 
  hardware, 
  accessories, 
  woodJointOptions,
  getColorById 
} from '../data/mockData';

// Yes/No Toggle Component
const YesNoToggle = ({ label, value, onChange }) => {
  const isYes = value === true;
  const isNo = value === false;
  
  return (
    <div className="flex items-center justify-between p-2 border border-gray-200 rounded-md bg-white hover:bg-gray-50 transition-colors">
      <Label className="text-xs text-gray-700 cursor-pointer flex-1">{label}</Label>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => onChange(true)}
          className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
            isYes
              ? 'bg-green-600 text-white shadow-sm'
              : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
          }`}
        >
          YES
        </button>
        <button
          type="button"
          onClick={() => onChange(false)}
          className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
            isNo
              ? 'bg-red-600 text-white shadow-sm'
              : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
          }`}
        >
          NO
        </button>
      </div>
    </div>
  );
};

const SavedQuotes = ({ open, onOpenChange }) => {
  const { savedQuotes, removeQuote, clearAllQuotes } = useQuotes();
  const [clientDetails, setClientDetails] = useState({
    name: '',
    address: '',
    city: '',
    postalCode: '',
    phone: '',
    email: '',
    notes: '',
    signature: '',
    // Additional Options
    clickOnColorAndSize: false,
    screenElectricallyOperated: false,
    rollerShutterElectricallyOperated: false,
    // Home Finishing
    buoySharing: false,
    typesetting: false,
    container: false,
    interiorFinish: false,
    // Lifting Support
    crane: false,
    craneTrips: '',
    liftingAssistance: false,
    liftingAssistancePersonnel: '',
    // Comments
    comments: ''
  });
  const [isEditing, setIsEditing] = useState(true);
  const [isSaved, setIsSaved] = useState(false);
  const quoteRef = useRef(null);
  const signatureCanvasRef = useRef(null);
  const [quoteNumber, setQuoteNumber] = useState('');
  const [isDrawing, setIsDrawing] = useState(false);

  // Generate quote number on mount
  useEffect(() => {
    if (open) {
      const date = new Date();
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const random = Math.floor(Math.random() * 1000);
      setQuoteNumber(`OFF-${year}${month}${day}-${random}`);
      setIsEditing(true);
      setIsSaved(false);
    }
  }, [open]);

  // Initialize signature canvas
  useEffect(() => {
    if (signatureCanvasRef.current && isEditing) {
      const canvas = signatureCanvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      
      if (clientDetails.signature) {
        const img = new Image();
        img.onload = () => {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0);
        };
        img.src = clientDetails.signature;
      }
    }
  }, [isEditing, clientDetails.signature]);

  // Format date
  const formatDate = (date) => {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    const d = new Date(date);
    return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
  };

  // Generate description items from saved quote
  const getDescriptionItemsForQuote = (quote) => {
    const items = [];
    const config = quote.config;
    
    // Dimensions
    items.push(`- Width: ${config.width} mm`);
    items.push(`- Height: ${config.height} mm`);
    items.push(`- Area: ${((config.width * config.height) / 1000000).toFixed(2)} m²`);
    
    // Detect page type
    const pageType = config.doorStyle !== undefined ? 'voordeur' :
                     config.designType !== undefined ? 'terrasdeur' :
                     config.topLight !== undefined || config.numberOfParts !== undefined ? 'achterdeur' :
                     config.doorType === 'sliding' || config.slidingPosition ? 'schuifpui' :
                     'kozijnen';
    
    // Profile System
    if (profileSystems && profileSystems.length > 0 && config.profileSystem) {
      const profileSystem = profileSystems.find(p => p.id === config.profileSystem);
      if (profileSystem) {
        items.push(`- ${profileSystem.name} frames`);
      }
    }
    
    // External Frame Color
    const externalColorId = config.exteriorColor || config.color;
    if (externalColorId) {
      const colorData = getColorById(externalColorId);
      const colorName = colorData?.name || (colorData?.ral ? `RAL ${colorData.ral}` : externalColorId);
      items.push(`- Exterior frame ${colorName}`);
    }
    
    // Interior Frame Color (for windows)
    const hasInteriorFrames = config.partWindowTypes?.some(type => 
      ['fixed-glass', 'tilt-only', 'tilt-turn-left', 'tilt-turn-right'].includes(type)
    ) || config.overheadPartWindowTypes?.some(type => 
      ['fixed-glass', 'tilt-only', 'tilt-turn-left', 'tilt-turn-right'].includes(type)
    );
    
    if (hasInteriorFrames && config.interiorFrameColor) {
      const interiorColorData = getColorById(config.interiorFrameColor);
      const interiorColorName = interiorColorData?.name || (interiorColorData?.ral ? `RAL ${interiorColorData.ral}` : config.interiorFrameColor);
      items.push(`- Interior frame ${interiorColorName}`);
    }
    
    // Interior Color (for doors)
    if (config.interiorColor && (pageType === 'schuifpui' || pageType === 'achterdeur' || pageType === 'voordeur' || pageType === 'terrasdeur')) {
      const interiorColorData = getColorById(config.interiorColor);
      const interiorColorName = interiorColorData?.name || (interiorColorData?.ral ? `RAL ${interiorColorData.ral}` : config.interiorColor);
      items.push(`- Interior frame ${interiorColorName}`);
    }
    
    // Glass Texture
    if (config.glassTexture) {
      const glassTexture = glassTextures.find(g => g.id === config.glassTexture);
      if (glassTexture) {
        items.push(`- ${glassTexture.name} glass`);
      }
    }
    
    // Glazing Type
    if (config.glazingType) {
      const glazingType = glazingTypes.find(g => g.id === config.glazingType);
      if (glazingType) {
        items.push(`- ${glazingType.name}`);
      }
    }
    
    // Sliding Door (Schuifpui) specific
    if (pageType === 'schuifpui' || config.doorType === 'sliding') {
      if (config.slidingPosition) {
        const slidingPositionNames = {
          'left': 'Sliding Section Left',
          'right': 'Sliding Section Right',
          'center': 'Sliding Section Center'
        };
        items.push(`- Sliding Position: ${slidingPositionNames[config.slidingPosition] || config.slidingPosition}`);
      }
      if (config.thresholdHeight) {
        items.push(`- Threshold: ${config.thresholdHeight}`);
      }
      if (config.leftGlassTexture) {
        const leftGlassTexture = glassTextures.find(g => g.id === config.leftGlassTexture);
        if (leftGlassTexture) {
          items.push(`- Glass (Left): ${leftGlassTexture.name}`);
        }
      }
      if (config.rightGlassTexture) {
        const rightGlassTexture = glassTextures.find(g => g.id === config.rightGlassTexture);
        if (rightGlassTexture) {
          items.push(`- Glass (Right): ${rightGlassTexture.name}`);
        }
      }
      if (config.rightGlazingType) {
        const rightGlazingType = glazingTypes.find(g => g.id === config.rightGlazingType);
        if (rightGlazingType) {
          items.push(`- Glazing (Right): ${rightGlazingType.name}`);
        }
      }
    }
    
    // Back Door (Achterdeur) specific
    if (pageType === 'achterdeur') {
      if (config.topLight) {
        const topLightNames = {
          'no-top-light': 'No Top Light',
          'with-top-light': 'With Top Light'
        };
        items.push(`- Top Light: ${topLightNames[config.topLight] || config.topLight}`);
      }
      if (config.numberOfParts) {
        const numberOfPartsNames = {
          'one-part': 'One Part',
          'two-parts': 'Two Parts'
        };
        items.push(`- Parts: ${numberOfPartsNames[config.numberOfParts] || config.numberOfParts}`);
      }
      if (config.numberOfParts === 'two-parts' && config.panelType) {
        const panelTypeNames = {
          'sandwich-no-mullions': 'Sandwich Panel (No Mullions)',
          'glass': 'Glass',
          'horizontal-mullions': 'Horizontal Mullions',
          'vertical-mullions': 'Vertical Mullions'
        };
        items.push(`- Panel Type: ${panelTypeNames[config.panelType] || config.panelType}`);
      }
      if (config.thresholdHeight) {
        items.push(`- Threshold: ${config.thresholdHeight}`);
      }
      if (config.parapet) {
        items.push(`- Parapet: ${config.parapet === 'with-parapet' ? 'With Parapet' : 'No Parapet'}`);
      }
      items.push(`- Security: SKG 2 Rating`);
      if (config.topLight === 'with-top-light' && config.overheadParts > 1) {
        items.push(`- ${config.overheadParts} Overhead Parts`);
        config.overheadPartWindowTypes?.forEach((type, index) => {
          const raamtype = raamtypes.find(r => r.id === type);
          if (raamtype) {
            items.push(`- Overhead Part ${index + 1}: ${raamtype.name}`);
          }
        });
      }
    }
    
    // Front Door (Voordeur) specific
    if (pageType === 'voordeur') {
      if (config.doorStyle) {
        const doorStyleNames = {
          'style-1': 'Style 1',
          'style-2': 'Style 2',
          'style-3': 'Style 3',
          'style-4': 'Style 4',
          'style-5': 'Style 5',
          'style-6': 'Style 6'
        };
        items.push(`- Door Style: ${doorStyleNames[config.doorStyle] || config.doorStyle}`);
      }
      if (config.parapet) {
        const parapetNames = {
          'standard-knob': 'Standard Door Knob',
          'rvs-bar': 'RVS Bar'
        };
        items.push(`- Handle: ${parapetNames[config.parapet] || config.parapet}`);
      }
      if (config.thresholdHeight) {
        items.push(`- Threshold: ${config.thresholdHeight}`);
      }
      items.push(`- Security: SKG 2 Rating`);
    }
    
    // Terrace Door (Terrasdeur) specific
    if (pageType === 'terrasdeur') {
      if (config.designType) {
        const designTypeNames = {
          'no-panel': 'No Panel',
          'left-panel': 'Left Panel',
          'right-panel': 'Right Panel',
          'both-panels': 'Both Panels'
        };
        items.push(`- Design: ${designTypeNames[config.designType] || config.designType}`);
      }
      if (config.doorType) {
        const doorTypeNames = {
          'normal': 'Normal',
          'top-light': 'Top Light'
        };
        items.push(`- Door Type: ${doorTypeNames[config.doorType] || config.doorType}`);
      }
      if (config.handlePosition) {
        const handlePositionNames = {
          'left': 'Handle Left',
          'right': 'Handle Right'
        };
        items.push(`- Handle Position: ${handlePositionNames[config.handlePosition] || config.handlePosition}`);
      }
      if (config.thresholdHeight) {
        items.push(`- Threshold: ${config.thresholdHeight}`);
      }
      if (config.parapet) {
        items.push(`- Parapet: ${config.parapet === 'with-parapet' ? 'With Parapet' : 'No Parapet'}`);
      }
      items.push(`- Security: SKG 2 Rating`);
      if (config.doorType === 'top-light' && config.overheadParts > 1) {
        items.push(`- ${config.overheadParts} Overhead Parts`);
        config.overheadPartWindowTypes?.forEach((type, index) => {
          const raamtype = raamtypes.find(r => r.id === type);
          if (raamtype) {
            items.push(`- Overhead Part ${index + 1}: ${raamtype.name}`);
          }
        });
      }
    }
    
    // Window configurator (Kozijnen) specific
    if (pageType === 'kozijnen') {
      if (config.divisionType) {
        items.push(`- Division: ${config.divisionType}`);
      }
      if (config.partWindowTypes && config.partWindowTypes.length > 0) {
        config.partWindowTypes.forEach((type, index) => {
          const raamtype = raamtypes.find(r => r.id === type);
          if (raamtype) {
            if (type === 'fixed-panel') {
              let panelInfo = [];
              const panelColorId = config.panelColors?.[index];
              if (panelColorId) {
                const panelColorData = getColorById(panelColorId);
                const panelColorName = panelColorData?.name || (panelColorData?.ral ? `RAL ${panelColorData.ral}` : panelColorId);
                panelInfo.push(panelColorName);
              } else {
                panelInfo.push('Anthracite Grey (RAL 7016)');
              }
              const panelLineType = config.panelLineTypes?.[index] || 'none';
              if (panelLineType === 'horizontal') {
                panelInfo.push('Horizontal Lines');
              } else if (panelLineType === 'vertical') {
                panelInfo.push('Vertical Lines');
              } else {
                panelInfo.push('Plain');
              }
              const panelInfoStr = panelInfo.length > 0 ? ` - ${panelInfo.join(' - ')}` : '';
              items.push(`- Part ${index + 1}: ${raamtype.name}${panelInfoStr}`);
            } else {
              items.push(`- Part ${index + 1}: ${raamtype.name}`);
            }
          }
        });
      }
      if (config.overhead === 'overhead') {
        items.push('- Overhead');
        if (config.overheadParts > 1) {
          items.push(`- ${config.overheadParts} Overhead Parts`);
        }
        config.overheadPartWindowTypes?.forEach((type, index) => {
          const raamtype = raamtypes.find(r => r.id === type);
          if (raamtype) {
            if (type === 'fixed-panel') {
              let panelInfo = [];
              const panelColorId = config.panelOverheadColors?.[index];
              if (panelColorId) {
                const panelColorData = getColorById(panelColorId);
                const panelColorName = panelColorData?.name || (panelColorData?.ral ? `RAL ${panelColorData.ral}` : panelColorId);
                panelInfo.push(panelColorName);
              } else {
                panelInfo.push('Anthracite Grey (RAL 7016)');
              }
              const panelLineType = config.panelOverheadLineTypes?.[index] || 'none';
              if (panelLineType === 'horizontal') {
                panelInfo.push('Horizontal Lines');
              } else if (panelLineType === 'vertical') {
                panelInfo.push('Vertical Lines');
              } else {
                panelInfo.push('Plain');
              }
              const panelInfoStr = panelInfo.length > 0 ? ` - ${panelInfo.join(' - ')}` : '';
              items.push(`- Overhead Part ${index + 1}: ${raamtype.name}${panelInfoStr}`);
            } else {
              items.push(`- Overhead Part ${index + 1}: ${raamtype.name}`);
            }
          }
        });
      }
      if (config.underlight === 'underlight') {
        items.push('- Underlight');
        if (config.underlightParts > 1) {
          items.push(`- ${config.underlightParts} Underlight Parts`);
        }
        config.underlightPartWindowTypes?.forEach((type, index) => {
          const raamtype = raamtypes.find(r => r.id === type);
          if (raamtype) {
            if (type === 'fixed-panel') {
              let panelInfo = [];
              const panelColorId = config.panelUnderlightColors?.[index];
              if (panelColorId) {
                const panelColorData = getColorById(panelColorId);
                const panelColorName = panelColorData?.name || (panelColorData?.ral ? `RAL ${panelColorData.ral}` : panelColorId);
                panelInfo.push(panelColorName);
              } else {
                panelInfo.push('Anthracite Grey (RAL 7016)');
              }
              const panelLineType = config.panelUnderlightLineTypes?.[index] || 'none';
              if (panelLineType === 'horizontal') {
                panelInfo.push('Horizontal Lines');
              } else if (panelLineType === 'vertical') {
                panelInfo.push('Vertical Lines');
              } else {
                panelInfo.push('Plain');
              }
              const panelInfoStr = panelInfo.length > 0 ? ` - ${panelInfo.join(' - ')}` : '';
              items.push(`- Underlight Part ${index + 1}: ${raamtype.name}${panelInfoStr}`);
            } else {
              items.push(`- Underlight Part ${index + 1}: ${raamtype.name}`);
            }
          }
        });
      }
    }
    
    // Wood Joint
    if (config.woodJoint && woodJointOptions && woodJointOptions.length > 0) {
      const woodJoint = woodJointOptions.find(w => w.id === config.woodJoint);
      if (woodJoint) {
        items.push(`- Joint: ${woodJoint.name}`);
      }
    }
    
    // Hardware
    if (config.hardware && Array.isArray(config.hardware) && hardware && hardware.length > 0) {
      config.hardware.forEach(hwId => {
        if (hwId) {
          const hw = hardware.find(h => h.id === hwId);
          if (hw) {
            items.push(`- Hardware: ${hw.name}`);
          }
        }
      });
    }
    
    // Accessories
    if (config.accessories && Array.isArray(config.accessories) && accessories && accessories.length > 0) {
      config.accessories.forEach(accId => {
        if (accId) {
          const acc = accessories.find(a => a.id === accId);
          if (acc) {
            items.push(`- Accessory: ${acc.name}`);
          }
        }
      });
    }
    
    // Standard included items
    items.push('- Installation');
    items.push('- Standard interior frame finishing');
    items.push('- Removal of old materials');
    items.push('- Police certification safe living');
    items.push('- SKG2** certification');
    items.push('- 15 years factory warranty on plastic frames');
    items.push('- 10 years supplier warranty on glass');
    items.push('- 10 years supplier warranty on window and door hardware manufacturer');
    items.push('- 5 years warranty on installation');
    
    return items;
  };

  // Calculate total price from all quotes
  const calculateTotalPrice = () => {
    return savedQuotes.reduce((total, quote) => {
      const quantity = quote.config.quantity || 1;
      return total + (quote.pricing?.totalPrice || 0) * quantity;
    }, 0);
  };

  // Calculate total area from all quotes
  const calculateTotalArea = () => {
    return savedQuotes.reduce((total, quote) => {
      const quantity = quote.config.quantity || 1;
      const area = parseFloat(quote.dimensions?.area || 0);
      return total + (area * quantity);
    }, 0);
  };

  const totalPrice = calculateTotalPrice();
  const totalArea = calculateTotalArea();
  const formatPrice = (price) => {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(price);
  };

  const handleExportPDF = async () => {
    if (!quoteRef.current) return;

    if (isEditing || !isSaved) {
      alert('Please save the client details by clicking "Save & Preview" before exporting the PDF.');
      return;
    }

    try {
      const editButtons = quoteRef.current.querySelectorAll('button.print\\:hidden, .print\\:hidden button, .print\\:hidden');
      const actionButtons = quoteRef.current.parentElement?.querySelectorAll('button') || [];
      const allButtonsToHide = [...editButtons, ...actionButtons];
      const originalDisplays = [];
      allButtonsToHide.forEach((btn, index) => {
        originalDisplays[index] = window.getComputedStyle(btn).display;
        btn.style.display = 'none';
      });

      quoteRef.current.classList.add('pdf-export');
      const style = document.createElement('style');
      style.textContent = `
        .pdf-export { font-size: 1.4em !important; }
        .pdf-export .text-xs { font-size: 0.9rem !important; }
        .pdf-export .text-sm { font-size: 1.1rem !important; }
        .pdf-export .text-base { font-size: 1.3rem !important; }
        .pdf-export .text-xl { font-size: 1.8rem !important; }
        .pdf-export .text-2xl { font-size: 2.2rem !important; }
        .pdf-export h1 { font-size: 2.2rem !important; }
        .pdf-export h2 { font-size: 1.8rem !important; }
        .pdf-export h3 { font-size: 1.3rem !important; }
        .pdf-export p { font-size: 0.9rem !important; }
        .pdf-export table { font-size: 1rem !important; }
        .pdf-export th, .pdf-export td { font-size: 1rem !important; padding: 0.75rem 1rem !important; }
      `;
      document.head.appendChild(style);

      await new Promise(resolve => setTimeout(resolve, 100));

      const canvas = await html2canvas(quoteRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        allowTaint: true,
        width: quoteRef.current.scrollWidth,
        height: quoteRef.current.scrollHeight
      });

      quoteRef.current.classList.remove('pdf-export');
      document.head.removeChild(style);

      allButtonsToHide.forEach((btn, index) => {
        btn.style.display = originalDisplays[index];
      });

      const imgData = canvas.toDataURL('image/png', 1.0);
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      const pxToMm = (25.4 / 96) / 2;
      const imgWidthMm = canvas.width * pxToMm;
      const imgHeightMm = canvas.height * pxToMm;
      
      const marginTop = 5;
      const marginBottom = 5;
      const marginSides = 3;
      const availableWidth = pdfWidth - (marginSides * 2);
      const availableHeight = pdfHeight - marginTop - marginBottom;
      
      const widthRatio = availableWidth / imgWidthMm;
      const heightRatio = availableHeight / imgHeightMm;
      const ratio = Math.min(widthRatio, heightRatio);
      
      const imgScaledWidth = imgWidthMm * ratio;
      const imgScaledHeight = imgHeightMm * ratio;
      
      const xOffset = (pdfWidth - imgScaledWidth) / 2;
      const yOffset = marginTop;

      pdf.addImage(imgData, 'PNG', xOffset, yOffset, imgScaledWidth, imgScaledHeight);

      const pdfBlob = pdf.output('blob');
      const pdfUrl = URL.createObjectURL(pdfBlob);
      window.open(pdfUrl, '_blank');
      
      setTimeout(() => {
        URL.revokeObjectURL(pdfUrl);
      }, 100);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('An error occurred while generating the PDF.');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[95vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Saved Quotes</DialogTitle>
        </DialogHeader>
        
        <div
          ref={quoteRef}
          className="bg-white p-6 pb-28 print:p-4"
          id="quote-content"
          style={{ maxHeight: '297mm', overflow: 'visible', width: '100%' }}
        >
          {/* Company Header */}
          <div className="mb-4 border-b-2 border-gray-300 pb-3">
            <div className="flex items-center justify-between mb-2">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">bestonline Kozijnen</h1>
                <p className="text-sm text-gray-700">Custom Plastic Frames</p>
              </div>
              <div className="text-right text-xs text-gray-700">
                <p>Binnenhavenstraat 41A</p>
                <p>7553 GH Hengelo</p>
                <p>Tel: +31 (0)6 33 03 30 82</p>
                <p>Email: Info@vmkkozijnen.nl</p>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div className="text-xs text-gray-700">
                <p><strong>Quote:</strong> {quoteNumber || 'OFF-' + new Date().getFullYear() + String(Math.floor(Math.random() * 1000000)).padStart(6, '0')}</p>
                <p><strong>Date:</strong> {formatDate(new Date())}</p>
                <p><strong>Valid Until:</strong> {formatDate(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000))}</p>
              </div>
            </div>
          </div>

          {/* Client Address Section */}
          <div className="mb-4">
            {isEditing ? (
              <div className="space-y-1">
                <Label htmlFor="clientAddress" className="text-xs">Client Address *</Label>
                <Input
                  id="clientAddress"
                  value={clientDetails.address || ''}
                  onChange={(e) => setClientDetails({...clientDetails, address: e.target.value})}
                  placeholder="Street address"
                  className="h-7 text-xs"
                />
                <div className="grid grid-cols-2 gap-1">
                  <div>
                    <Label htmlFor="clientPostalCode" className="text-xs">Postal Code *</Label>
                    <Input
                      id="clientPostalCode"
                      value={clientDetails.postalCode || ''}
                      onChange={(e) => setClientDetails({...clientDetails, postalCode: e.target.value})}
                      placeholder="1234 AB"
                      className="h-7 text-xs"
                    />
                  </div>
                  <div>
                    <Label htmlFor="clientCity" className="text-xs">City *</Label>
                    <Input
                      id="clientCity"
                      value={clientDetails.city || ''}
                      onChange={(e) => setClientDetails({...clientDetails, city: e.target.value})}
                      placeholder="City"
                      className="h-7 text-xs"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-1">
                  <div>
                    <Label htmlFor="clientPhone" className="text-xs">Phone</Label>
                    <Input
                      id="clientPhone"
                      type="tel"
                      value={clientDetails.phone || ''}
                      onChange={(e) => setClientDetails({...clientDetails, phone: e.target.value})}
                      placeholder="Phone"
                      className="h-7 text-xs"
                    />
                  </div>
                  <div>
                    <Label htmlFor="clientEmail" className="text-xs">Email</Label>
                    <Input
                      id="clientEmail"
                      type="email"
                      value={clientDetails.email || ''}
                      onChange={(e) => setClientDetails({...clientDetails, email: e.target.value})}
                      placeholder="Email"
                      className="h-7 text-xs"
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-xs text-gray-700">
                {clientDetails.address || clientDetails.postalCode || clientDetails.city || clientDetails.phone || clientDetails.email ? (
                  <>
                    {clientDetails.address && <p>{clientDetails.address}</p>}
                    {(clientDetails.postalCode || clientDetails.city) && (
                      <p>{clientDetails.postalCode} {clientDetails.city}</p>
                    )}
                    {clientDetails.phone && <p>Phone: {clientDetails.phone}</p>}
                    {clientDetails.email && <p>Email: {clientDetails.email}</p>}
                  </>
                ) : (
                  <p className="text-gray-400 italic">Client address</p>
                )}
                {!isEditing && (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="flex items-center gap-1 text-orange-600 hover:text-orange-700 print:hidden text-xs mt-1"
                    title="Edit Address"
                  >
                    <Pencil className="w-3 h-3" />
                    <span>Edit Address</span>
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Additional Details Section */}
          <div className="mb-6 border-t pt-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold text-gray-800">Additional Details</h3>
              {!isEditing && (
                <button
                  onClick={() => setIsEditing(true)}
                  className="flex items-center gap-1 text-orange-600 hover:text-orange-700 print:hidden text-xs"
                  title="Edit Additional Details"
                >
                  <Pencil className="w-3 h-3" />
                  <span>Edit</span>
                </button>
              )}
            </div>
            
            {isEditing ? (
              <>
                {/* Home Finishing */}
                <div className="mb-4">
                  <h4 className="text-sm font-semibold mb-2 text-gray-700">Home Finishing</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <YesNoToggle
                      label="Buoy sharing"
                      value={clientDetails.buoySharing}
                      onChange={(val) => setClientDetails({...clientDetails, buoySharing: val})}
                    />
                    <YesNoToggle
                      label="Typesetting"
                      value={clientDetails.typesetting}
                      onChange={(val) => setClientDetails({...clientDetails, typesetting: val})}
                    />
                    <YesNoToggle
                      label="Container"
                      value={clientDetails.container}
                      onChange={(val) => setClientDetails({...clientDetails, container: val})}
                    />
                    <YesNoToggle
                      label="Interior finish"
                      value={clientDetails.interiorFinish}
                      onChange={(val) => setClientDetails({...clientDetails, interiorFinish: val})}
                    />
                  </div>
                </div>

                {/* Lifting Support */}
                <div className="mb-4">
                  <h4 className="text-sm font-semibold mb-2 text-gray-700">Lifting Support</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <YesNoToggle
                      label="Crane"
                      value={clientDetails.crane}
                      onChange={(val) => setClientDetails({...clientDetails, crane: val, craneTrips: val ? clientDetails.craneTrips : ''})}
                    />
                    <YesNoToggle
                      label="Lifting assistance"
                      value={clientDetails.liftingAssistance}
                      onChange={(val) => setClientDetails({...clientDetails, liftingAssistance: val, liftingAssistancePersonnel: val ? clientDetails.liftingAssistancePersonnel : ''})}
                    />
                  </div>
                  {/* Crane trips input - only when crane is YES */}
                  {clientDetails.crane === true && (
                    <div className="mt-2">
                      <Label htmlFor="craneTrips" className="text-xs font-semibold mb-1 block text-gray-700">Number of trips</Label>
                      <Input
                        id="craneTrips"
                        type="number"
                        min="1"
                        value={clientDetails.craneTrips || ''}
                        onChange={(e) => setClientDetails({...clientDetails, craneTrips: e.target.value})}
                        className="w-full"
                        placeholder="Enter number of trips"
                      />
                    </div>
                  )}
                  {/* Lifting assistance personnel input - only when lifting assistance is YES */}
                  {clientDetails.liftingAssistance === true && (
                    <div className="mt-2">
                      <Label htmlFor="liftingAssistancePersonnel" className="text-xs font-semibold mb-1 block text-gray-700">Personnel (Max 3)</Label>
                      <Input
                        id="liftingAssistancePersonnel"
                        type="number"
                        min="1"
                        max="3"
                        value={clientDetails.liftingAssistancePersonnel || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '' || (parseInt(value) >= 1 && parseInt(value) <= 3)) {
                            setClientDetails({...clientDetails, liftingAssistancePersonnel: value});
                          }
                        }}
                        className="w-full"
                        placeholder="Enter number of personnel (1-3)"
                      />
                    </div>
                  )}
                </div>

                {/* Comments */}
                <div className="mb-4">
                  <Label htmlFor="comments" className="text-xs font-semibold mb-2 block text-gray-700">Comments</Label>
                  <Textarea
                    id="comments"
                    value={clientDetails.comments || ''}
                    onChange={(e) => setClientDetails({...clientDetails, comments: e.target.value})}
                    placeholder="Enter any additional comments or notes..."
                    className="text-xs min-h-[80px]"
                    rows={4}
                  />
                </div>
              </>
            ) : (
              <div className="text-xs text-gray-700 space-y-3">

                {/* Home Finishing Display */}
                <div>
                  <h4 className="text-sm font-semibold mb-2 text-gray-700">Home Finishing</h4>
                  <div className="space-y-1">
                    <p className="flex justify-between">
                      <span>Buoy sharing:</span>
                      <span className={`font-medium ${clientDetails.buoySharing ? 'text-green-600' : 'text-red-600'}`}>
                        {clientDetails.buoySharing ? 'YES' : 'NO'}
                      </span>
                    </p>
                    <p className="flex justify-between">
                      <span>Typesetting:</span>
                      <span className={`font-medium ${clientDetails.typesetting ? 'text-green-600' : 'text-red-600'}`}>
                        {clientDetails.typesetting ? 'YES' : 'NO'}
                      </span>
                    </p>
                    <p className="flex justify-between">
                      <span>Container:</span>
                      <span className={`font-medium ${clientDetails.container ? 'text-green-600' : 'text-red-600'}`}>
                        {clientDetails.container ? 'YES' : 'NO'}
                      </span>
                    </p>
                    <p className="flex justify-between">
                      <span>Interior finish:</span>
                      <span className={`font-medium ${clientDetails.interiorFinish ? 'text-green-600' : 'text-red-600'}`}>
                        {clientDetails.interiorFinish ? 'YES' : 'NO'}
                      </span>
                    </p>
                  </div>
                </div>

                {/* Lifting Support Display */}
                <div>
                  <h4 className="text-sm font-semibold mb-2 text-gray-700">Lifting Support</h4>
                  <div className="space-y-1">
                    <p className="flex justify-between">
                      <span>Crane:</span>
                      <span className={`font-medium ${clientDetails.crane ? 'text-green-600' : 'text-red-600'}`}>
                        {clientDetails.crane ? 'YES' : 'NO'}
                      </span>
                    </p>
                    {clientDetails.crane === true && clientDetails.craneTrips && (
                      <p className="flex justify-between ml-4 text-xs">
                        <span>Number of trips:</span>
                        <span className="font-medium">{clientDetails.craneTrips}</span>
                      </p>
                    )}
                    <p className="flex justify-between">
                      <span>Lifting assistance:</span>
                      <span className={`font-medium ${clientDetails.liftingAssistance ? 'text-green-600' : 'text-red-600'}`}>
                        {clientDetails.liftingAssistance ? 'YES' : 'NO'}
                      </span>
                    </p>
                    {clientDetails.liftingAssistance === true && clientDetails.liftingAssistancePersonnel && (
                      <p className="flex justify-between ml-4 text-xs">
                        <span>Personnel:</span>
                        <span className="font-medium">{clientDetails.liftingAssistancePersonnel}</span>
                      </p>
                    )}
                  </div>
                </div>

                {/* Comments Display */}
                {clientDetails.comments && (
                  <div>
                    <h4 className="text-sm font-semibold mb-2 text-gray-700">Comments</h4>
                    <p className="whitespace-pre-wrap bg-gray-50 p-2 rounded border border-gray-200">{clientDetails.comments}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Letter/Description Section */}
          <div className="mb-6 text-sm text-gray-800 leading-relaxed">
            <h2 className="text-lg font-bold mb-3">Your Quote for the Installation of Plastic Facade Elements</h2>
            
            <div className="mb-3">
              <div className="flex items-center gap-2 mb-1">
                <Label htmlFor="clientName" className="text-xs">Dear Sir/Madam *</Label>
                {!isEditing && (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="flex items-center gap-1 text-orange-600 hover:text-orange-700 print:hidden text-xs"
                    title="Edit Name"
                  >
                    <Pencil className="w-3 h-3" />
                    <span>Edit</span>
                  </button>
                )}
              </div>
              {isEditing ? (
                <Input
                  id="clientName"
                  value={clientDetails.name || ''}
                  onChange={(e) => setClientDetails({...clientDetails, name: e.target.value})}
                  placeholder="Name"
                  className="mb-3 h-8 text-sm"
                />
              ) : (
                <p className="mb-3">
                  <strong>Dear Sir/Madam {clientDetails.name || '[Name]'},</strong>
                </p>
              )}
            </div>

            <div className="space-y-3 mb-4">
              <p>
                Thank you for the pleasant conversation during our advisor's visit. We have discussed your wishes and ideas together
                and look forward to converting them into a sustainable and aesthetic solution for your home.
              </p>
              <p>
                Based on our visit and the information you shared, we are pleased to offer you a proposal for the
                installation of high-quality plastic facade elements.
              </p>
            </div>

            <div className="mb-4">
              <h3 className="font-bold mb-2">Our Approach</h3>
              <p className="mb-2">
                We work exclusively with top-quality plastic facade elements. These are low-maintenance,
                weather-resistant, and offer excellent insulation values.
              </p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li><strong>Custom Made:</strong> Each element is fully custom-made, matching the appearance of your home and your personal preferences.</li>
                <li><strong>Professional Installation:</strong> Our experienced installers ensure professional placement, with precision and attention to detail as priorities.</li>
              </ul>
            </div>

            <div className="mb-4">
              <h3 className="font-bold mb-2">Benefits of Plastic Facade Elements</h3>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Long lifespan and minimal maintenance costs.</li>
                <li>Available in various colors and designs, for a modern or classic appearance.</li>
                <li>Excellent insulation, which contributes to lower energy bills.</li>
                <li>Resistant to all weather conditions and colorfast.</li>
              </ul>
            </div>

            <div className="mb-4">
              <h3 className="font-bold mb-2">Warranty</h3>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>15 years factory warranty on plastic frames</li>
                <li>10 years supplier warranty on glass</li>
                <li>10 years supplier warranty on window and door hardware manufacturer</li>
                <li>5 years warranty on installation</li>
              </ul>
              <p className="mt-2">
                <strong>Police certification safe living</strong> - SKG2** certification
              </p>
            </div>
          </div>

          {/* Pricing Table with All Quotes */}
          <div className="mb-6">
            <table className="w-full border-collapse border border-gray-300 text-sm">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 px-3 py-2 text-left font-semibold">Description</th>
                  <th className="border border-gray-300 px-3 py-2 text-right font-semibold">Amount</th>
                  <th className="border border-gray-300 px-3 py-2 text-right font-semibold">Total</th>
                  <th className="border border-gray-300 px-3 py-2 text-right font-semibold">VAT</th>
                </tr>
              </thead>
              <tbody>
                {savedQuotes.map((quote, quoteIndex) => {
                  // Ensure quantity is read from saved config, default to 1 if not present
                  const quantity = (quote.config.quantity !== undefined && quote.config.quantity !== null) ? quote.config.quantity : 1;
                  const descriptionItems = getDescriptionItemsForQuote(quote);
                  const quotePrice = (quote.pricing?.totalPrice || 0) * quantity;
                  
                  return (
                    <React.Fragment key={quote.id}>
                      <tr>
                        <td className="border border-gray-300 px-3 py-2" colSpan="4">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <div className="font-semibold mb-1">
                                {quantity} x Plastic frames
                              </div>
                              <div className="text-xs space-y-0.5 mt-1">
                                <div>Including</div>
                                {descriptionItems.map((item, index) => (
                                  <div key={index}>{item}</div>
                                ))}
                              </div>
                            </div>
                            <div className="flex flex-col items-center justify-center">
                              {quote.svgData ? (
                                <>
                                  <div className="mb-2 text-sm font-semibold text-gray-700">
                                    Quote {quoteIndex + 1} - A{quoteIndex + 1}
                                  </div>
                                  <div 
                                    dangerouslySetInnerHTML={{ __html: quote.svgData }} 
                                    className="max-w-full"
                                    style={{ transform: 'scale(0.75)', transformOrigin: 'top left' }}
                                  />
                                </>
                              ) : (
                                <p className="text-gray-500 text-xs">Drawing not available</p>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td className="border border-gray-300 px-3 py-2"></td>
                        <td className="border border-gray-300 px-3 py-2 text-right"></td>
                        <td className="border border-gray-300 px-3 py-2 text-right font-semibold">
                          {formatPrice(quotePrice)}
                        </td>
                        <td className="border border-gray-300 px-3 py-2 text-right">21%</td>
                      </tr>
                      {quoteIndex < savedQuotes.length - 1 && (
                        <tr>
                          <td className="border border-gray-300 px-3 py-2" colSpan="4" style={{ height: '20px' }}></td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                <tr>
                  <td className="border border-gray-300 px-3 py-2 font-semibold" colSpan="2">Total Area</td>
                  <td className="border border-gray-300 px-3 py-2 text-right font-semibold">
                    {totalArea.toFixed(2)} m²
                  </td>
                  <td className="border border-gray-300 px-3 py-2"></td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 px-3 py-2 font-semibold" colSpan="2">Subtotal</td>
                  <td className="border border-gray-300 px-3 py-2 text-right font-semibold">
                    {formatPrice(totalPrice)}
                  </td>
                  <td className="border border-gray-300 px-3 py-2"></td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-3 py-2" colSpan="2">21% VAT</td>
                  <td className="border border-gray-300 px-3 py-2 text-right">
                    {formatPrice(totalPrice * 0.21)}
                  </td>
                  <td className="border border-gray-300 px-3 py-2"></td>
                </tr>
                <tr className="bg-orange-50 font-bold">
                  <td className="border border-gray-300 px-3 py-2" colSpan="2">Total</td>
                  <td className="border border-gray-300 px-3 py-2 text-right text-orange-600">
                    {formatPrice(totalPrice * 1.21)}
                  </td>
                  <td className="border border-gray-300 px-3 py-2"></td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Closing Text */}
          <div className="mb-4 text-sm text-gray-800 leading-relaxed">
            <div className="mb-3">
              <h3 className="font-bold mb-2">The Steps</h3>
              <p className="mb-2">
                If you agree to this quote, we will:
              </p>
              <ol className="list-decimal list-inside space-y-1 ml-2">
                <li>Schedule a final measurement appointment to determine the exact dimensions.</li>
                <li>Create a planning together with you for the execution.</li>
                <li>Carry out our work within the agreed timeframe.</li>
              </ol>
            </div>
            <p className="mb-3">
              We stand for quality and service, and look forward to beautifying and making your home more sustainable
              with our plastic facade elements. If you have any questions or would like further clarification, please do not
              hesitate to contact us.
            </p>
            <p className="mb-2">
              We hope that our quote meets your wishes and that we have informed you sufficiently. We naturally hope
              that you will choose bestonline Kozijnen.
            </p>
            <p className="mt-4">
              <strong>Kind regards,</strong><br />
              bestonline Kozijnen
            </p>
          </div>

          {/* Signature Section */}
          <div className="mt-8 pt-4 border-t-2 border-gray-300">
            <Label className="text-sm font-semibold mb-2 block">Client Signature</Label>
            {isEditing ? (
              <div>
                <canvas
                  ref={signatureCanvasRef}
                  width={500}
                  height={100}
                  className="border-2 border-gray-300 rounded cursor-crosshair bg-white"
                  style={{ width: '100%', maxWidth: '500px', height: '100px' }}
                  onMouseDown={(e) => {
                    setIsDrawing(true);
                    const canvas = signatureCanvasRef.current;
                    if (!canvas) return;
                    const rect = canvas.getBoundingClientRect();
                    const scaleX = canvas.width / rect.width;
                    const scaleY = canvas.height / rect.height;
                    const ctx = canvas.getContext('2d');
                    ctx.strokeStyle = '#000000';
                    ctx.lineWidth = 2;
                    ctx.lineCap = 'round';
                    ctx.lineJoin = 'round';
                    ctx.beginPath();
                    ctx.moveTo((e.clientX - rect.left) * scaleX, (e.clientY - rect.top) * scaleY);
                  }}
                  onMouseMove={(e) => {
                    if (!isDrawing) return;
                    const canvas = signatureCanvasRef.current;
                    if (!canvas) return;
                    const rect = canvas.getBoundingClientRect();
                    const scaleX = canvas.width / rect.width;
                    const scaleY = canvas.height / rect.height;
                    const ctx = canvas.getContext('2d');
                    ctx.lineTo((e.clientX - rect.left) * scaleX, (e.clientY - rect.top) * scaleY);
                    ctx.stroke();
                  }}
                  onMouseUp={() => {
                    setIsDrawing(false);
                    const canvas = signatureCanvasRef.current;
                    if (canvas) {
                      const signatureData = canvas.toDataURL('image/png');
                      setClientDetails({...clientDetails, signature: signatureData});
                    }
                  }}
                  onMouseLeave={() => setIsDrawing(false)}
                  onTouchStart={(e) => {
                    e.preventDefault();
                    setIsDrawing(true);
                    const canvas = signatureCanvasRef.current;
                    if (!canvas) return;
                    const rect = canvas.getBoundingClientRect();
                    const scaleX = canvas.width / rect.width;
                    const scaleY = canvas.height / rect.height;
                    const ctx = canvas.getContext('2d');
                    ctx.strokeStyle = '#000000';
                    ctx.lineWidth = 2;
                    ctx.lineCap = 'round';
                    ctx.lineJoin = 'round';
                    const touch = e.touches[0];
                    ctx.beginPath();
                    ctx.moveTo((touch.clientX - rect.left) * scaleX, (touch.clientY - rect.top) * scaleY);
                  }}
                  onTouchMove={(e) => {
                    e.preventDefault();
                    if (!isDrawing) return;
                    const canvas = signatureCanvasRef.current;
                    if (!canvas) return;
                    const rect = canvas.getBoundingClientRect();
                    const scaleX = canvas.width / rect.width;
                    const scaleY = canvas.height / rect.height;
                    const ctx = canvas.getContext('2d');
                    const touch = e.touches[0];
                    ctx.lineTo((touch.clientX - rect.left) * scaleX, (touch.clientY - rect.top) * scaleY);
                    ctx.stroke();
                  }}
                  onTouchEnd={(e) => {
                    e.preventDefault();
                    setIsDrawing(false);
                    const canvas = signatureCanvasRef.current;
                    if (canvas) {
                      const signatureData = canvas.toDataURL('image/png');
                      setClientDetails({...clientDetails, signature: signatureData});
                    }
                  }}
                />
                <div className="flex gap-2 mt-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const canvas = signatureCanvasRef.current;
                      if (canvas) {
                        const ctx = canvas.getContext('2d');
                        ctx.clearRect(0, 0, canvas.width, canvas.height);
                        setClientDetails({...clientDetails, signature: ''});
                      }
                    }}
                    className="text-xs"
                  >
                    Clear
                  </Button>
                  <p className="text-xs text-gray-600 self-center">Please sign above before exporting</p>
                </div>
              </div>
            ) : (
              <div>
                {clientDetails.signature && clientDetails.signature.startsWith('data:image') ? (
                  <img src={clientDetails.signature} alt="Signature" className="border-b-2 border-gray-400 pb-1 max-h-[100px] max-w-full object-contain" />
                ) : (
                  <div className="border-b-2 border-gray-400 pb-1 h-[100px] flex items-center justify-center text-gray-400 italic">
                    No signature
                  </div>
                )}
                <button
                  onClick={() => setIsEditing(true)}
                  className="flex items-center gap-1 text-orange-600 hover:text-orange-700 print:hidden text-xs mt-2"
                  title="Edit Signature"
                >
                  <Pencil className="w-3 h-3" />
                  <span>Edit</span>
                </button>
              </div>
            )}
          </div>

          {/* Save & Preview Button */}
          {isEditing && (
            <div className="mt-6 mb-4 print:hidden">
              <Button 
                onClick={() => {
                  setIsEditing(false);
                  setIsSaved(true);
                }}
                className="bg-orange-600 hover:bg-orange-700 h-8 text-sm px-4"
              >
                Save & Preview
              </Button>
            </div>
          )}

          {/* Remove Quote Buttons - Under Table */}
          {savedQuotes.length > 0 && (
            <div className="mt-4 mb-4 print:hidden">
              <div className="flex flex-wrap gap-2">
                {savedQuotes.map((quote, index) => (
                  <Button
                    key={quote.id}
                    onClick={() => {
                      if (confirm('Are you sure you want to remove this quote?')) {
                        removeQuote(quote.id);
                      }
                    }}
                    variant="outline"
                    size="sm"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Remove Quote {index + 1}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div
            className="flex justify-end gap-4 mt-6 mb-4 print:hidden"
            style={{ width: '100%' }}
          >
            <Button variant="outline" onClick={handlePrint}>
              Print
            </Button>
            <Button 
              onClick={handleExportPDF} 
              className="bg-orange-600 hover:bg-orange-700"
              disabled={isEditing || !isSaved || !clientDetails.signature}
            >
              Export as PDF
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SavedQuotes;
